import { Controller } from "@hotwired/stimulus";

// Pairs two jQuery UI date pickers so the end date can't be set before the
// start date, or the start date after the end date.
//
// Optional settings (formats use jQuery UI's codes: yy = 2026, y = 26, mm = 03, dd = 05):
//
//   data-date-range-format-value
//     How the picked date appears in the start/end inputs. Defaults to
//     "mm/dd/yy" (03/05/2026).
//
//   data-date-range-alt-format-value
//     For pages whose server expects a different format than users see. Add
//     hidden startAlt/endAlt inputs and the picker also writes the date there,
//     in this format, for the form to submit. E.g. "yy-mm-dd" (2026-03-05).
//
// A date typed over or erased by hand is re-read when its input changes: an
// unreadable date is cleared, along with its alt input, so it stops filtering.
export default class extends Controller {
  static targets = ["start", "end", "startAlt", "endAlt"];
  static values = { format: { type: String, default: "mm/dd/yy" }, altFormat: String };

  connect() {
    this.$ = window.jQuery;
    if (!this.$ || !this.$.fn.datepicker) return;

    this.start = this.$(this.startTarget).datepicker(this.#options(this.startTarget, this.#altFor(this.startTarget)));
    this.end = this.$(this.endTarget).datepicker(this.#options(this.endTarget, this.#altFor(this.endTarget)));
    this.element.addEventListener("change", this.#onChange);
    this.#limitRange();
  }

  disconnect() {
    if (!this.start) return;

    this.element.removeEventListener("change", this.#onChange);
    this.start.add(this.end).datepicker("destroy");
  }

  #options(input, altField) {
    const options = {
      dateFormat: this.formatValue,
      // A native event, so Stimulus actions (e.g. the overlay's Clear button)
      // hear it; jQuery's own trigger only reaches jQuery handlers.
      onSelect: () => input.dispatchEvent(new Event("change", { bubbles: true }))
    };
    if (altField) Object.assign(options, { altField, altFormat: this.#altFormat });

    return options;
  }

  #onChange = ({ target }) => {
    if (target === this.startTarget || target === this.endTarget) this.#sync(target);
    this.#limitRange();
  };

  // The alt input only updates when the picker reads a date, so an erased or
  // mistyped date would still submit the old one.
  #sync(input) {
    const date = this.#parse(input.value);
    if (!date) input.value = "";

    const alt = this.#altFor(input);
    if (alt) alt.value = date ? this.$.datepicker.formatDate(this.#altFormat, date) : "";
  }

  #limitRange = () => {
    this.end.datepicker("option", "minDate", this.start.datepicker("getDate"));
    this.start.datepicker("option", "maxDate", this.end.datepicker("getDate"));
  };

  #altFor(input) {
    if (input === this.startTarget) return this.hasStartAltTarget && this.startAltTarget;

    return this.hasEndAltTarget && this.endAltTarget;
  }

  get #altFormat() {
    return this.altFormatValue || this.formatValue;
  }

  #parse(value) {
    try {
      return this.$.datepicker.parseDate(this.formatValue, value);
    } catch {
      return null;
    }
  }
}
